import type { GeneratedMedia } from '~entities/media';
import type { TMessage } from '~entities/messages';
import type { TSuggestedTextItem } from '~entities/suggestedText';

export type TEvent = {
	label: string;
	type: string;
	payload: Record<string, string | number>;
	details: Record<string, string | number>;
	sync: string;
};

export enum EventType {
	ChatMessageReceived = 'message:message:v2',
	ChatMessageUpdated = 'updated:message:v2',
	NeuronsOrSubscriptionBought = 'event.credits.mall.receipt.exchanged',
	BalanceChanged = 'account:credits',
	NeuronsReceived = 'event.credits.mall.exchanged',
	UserTagSet = 'event.users.tags.set',
	UserTagUnset = 'event.users.tags.unset',
	UserExperimentSet = 'event.users.tags.experiments.set',
	LevelChanged = 'event.users.experience.updated..ai',
	ChatBecomeAvailable = 'event.ai.chats.bot.available.became',
	ChatBecomeUnavailable = 'event.ai.chats.bot.unavailable.became',
	OneClickPaymentCompleted = 'event.exo.moto.payment.oneclick.completed',
	ImageUpdated = 'event.ai.image.generation.updated.enriched',
	VideoUpdated = 'event.ai.video.generation.updated.enriched',
	ImageGenerated = 'event.ai.image.generation.finished.enriched',
	VideoGenerated = 'event.ai.video.generation.finished.enriched',
	ChatMediaGenerationStarted = 'event.dialogs.media.generation.initiated',
	SuggestedTextsGenerated = 'event.ai.suggestions.generated',
	ChatBotBlocked = 'event.ai.chats.feature.added..blocked',
	ChatBotUnblocked = 'event.ai.chats.feature.removed..blocked',
}

type TCreditsChanged = {
	change: number;
	balance: number;
	meta?: {
		cause?: 'credits.mall.exchange';
		reason?: 'next-level';
	};
};

type TTagChange = {
	tag: string;
};

type TPaymentProcessed = {
	amount: number;
	autoRefill?: {
		enable: boolean;
	};
	meta: {
		subscription: boolean;
		description: string;
	};
};

type TLevelChanged = {
	userId: string;
	current: {
		currentPoints: number;
		nextLevelPoints: number;
		level: number;
	};
	changes: {
		experienceChanged: number;
		levelChanged: number;
		levelChangedMessage?: string;
		reason: {
			messageTag: string;
		};
	};
};

type TSuggestedTextSocketPayload = {
	userId: string;
	botId: string;
	messageId: number;
	suggestions: TSuggestedTextItem[];
};

type TChatAvailabilityChanged = {
	userId: string;
	botId: string;
	botUid: string;
};

type TOneClickPaymentCompleted = {
	paymentId: string;
	status: 'completed' | 'rejected';
	message?: string;
};

type ImageGeneratedPayload = {
	generated: GeneratedMedia;
};

type VideoGeneratedPayload = {
	generated: GeneratedMedia;
};

type ChatMediaGenerationPayload = {
	botId: string;
	mediaType: 'photo' | 'video';
	tag: string;
};

type ChatBotBlockedPayload = {
	userId: string;
	botIds: string[];
};

export type EventMsg =
	| { type: EventType.ChatMessageReceived; payload: TMessage }
	| { type: EventType.ChatMessageUpdated; payload: TMessage }
	| { type: EventType.BalanceChanged; payload: TCreditsChanged }
	| { type: EventType.NeuronsReceived; payload: TCreditsChanged }
	| { type: EventType.UserTagSet; payload: TTagChange }
	| { type: EventType.UserTagUnset; payload: TTagChange }
	| { type: EventType.UserExperimentSet; payload: TTagChange }
	| {
			type: EventType.NeuronsOrSubscriptionBought;
			payload: TPaymentProcessed;
	  }
	| { type: EventType.LevelChanged; payload: TLevelChanged }
	| {
			type: EventType.ChatBecomeAvailable;
			payload: TChatAvailabilityChanged;
	  }
	| {
			type: EventType.ChatBecomeUnavailable;
			payload: TChatAvailabilityChanged;
	  }
	| {
			type: EventType.OneClickPaymentCompleted;
			payload: TOneClickPaymentCompleted;
	  }
	| {
			type: EventType.ImageGenerated;
			payload: ImageGeneratedPayload;
	  }
	| {
			type: EventType.VideoGenerated;
			payload: VideoGeneratedPayload;
	  }
	| {
			type: EventType.ChatMediaGenerationStarted;
			payload: ChatMediaGenerationPayload;
	  }
	| {
			type: EventType.SuggestedTextsGenerated;
			payload: TSuggestedTextSocketPayload;
	  }
	| {
			type: EventType.ChatBotBlocked;
			payload: ChatBotBlockedPayload;
	  }
	| {
			type: EventType.ChatBotUnblocked;
			payload: ChatBotBlockedPayload;
	  };

export const isMsgType = <T extends EventType>(
	msg: EventMsg,
	...types: T[]
): msg is Extract<EventMsg, { type: T }> =>
	types.some((type) => type === msg.type);
